import React, { useEffect, ChangeEvent, useState } from 'react';

import classNames from 'classnames';

import { BonusInfoLabel } from './bonus-info-label';

interface IBonusInputProps {
  min?: number;
  max?: number;
  maxPercent?: number;
  value: number | null;
  onChanged?: (val: number | null) => void;
  disabled?: boolean; // 👈 Add the disabled prop
}

const BonusInput: React.FC<IBonusInputProps> = ({
  min = 0,
  max = 0,
  maxPercent = 0,
  onChanged,
  value,
  disabled = false, // 👈 Set default value to false
}) => {
  const [forceRerender, setForceRerender] = useState(0);
  // Ensure value stays within bounds and is valid on mount or prop change
  useEffect(() => {
    // Check if the value is a valid number and within bounds.
    // Use `null` to represent an empty or invalid state.
    if (value) {
      const numeric = Number.parseFloat(`${value}`);
      const isInvalidValue = Number.isNaN(numeric);

      if (isInvalidValue) {
        onChanged?.(min);
      } else if (numeric > max) {
        onChanged?.(max);
      } else {
        if (numeric < min) {
          onChanged?.(min);
        }
      }
    }
  }, [min, max, value, onChanged]);

  // Handle changes from the input field
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;

    const inputValue = e.target.value;
    if (inputValue === '') {
      onChanged?.(null);
    } else {
      const numericValue = parseFloat(inputValue);
      if (!Number.isNaN(numericValue)) {
        onChanged?.(numericValue);
      } else {
        setForceRerender((x: number) => x + 1);
      }
    }
  };

  // Handle the blur event to enforce min/max bounds
  const handleBlur = () => {
    if (disabled) return; // 👈 Do nothing if disabled

    if (value === null || value < min) {
      onChanged?.(min);
    } else if (value > max) {
      onChanged?.(max);
    }
  };

  const handleMaxClick = () => {
    if (disabled) return; // 👈 Do nothing if disabled
    onChanged?.(max);
  };

  return (
    <div className="flex flex-col items-center w-full">
      <div className="relative flex items-center mb-1 w-full h-[34px] border-b-2 border-gray-300">
        <input
          value={value ? (value === 0 ? '' : value) : ''}
          onChange={handleChange}
          onBlur={handleBlur}
          min={min}
          max={max}
          disabled={disabled}
          className={classNames(
            'absolute top-0 left-0 right-0 bottom-0 text-left font-semibold outline-none text-[32px] bg-transparent',
            {
              'text-gray-400 cursor-not-allowed': disabled,
              'text-gray-600': !disabled,
            },
          )}
        />
        <button
          onClick={handleMaxClick}
          disabled={disabled} // 👈 Apply the disabled prop
          className={classNames('absolute right-0 bottom-1 text-sm font-semibold cursor-pointer text-gray-600', {
            invisible: disabled,
            'cursor-pointer text-gray-[#585858]': !disabled,
          })}
        >
          макс.
        </button>
        {!value && max > 0 && (
          <div
            className="absolute top-0 left-0 right-0 bottom-0 bg-transparent flex items-center"
            style={{ pointerEvents: 'none' }}
          >
            <p className="font-manrope text-[18px] font-medium text-[#808080]">{`бонусами от ${min} до ${max}`}</p>
          </div>
        )}
      </div>

      <BonusInfoLabel limit={maxPercent} classes={'text-[#FAD355] !text-[13px] !font-medium'} />
    </div>
  );
};

export default BonusInput;
